import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';

import { AddItemProcessToQuoteDialogComponent } from '@components/quotes/add-item-process-to-quote-dialog/add-item-process-to-quote-dialog.component';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, skip, startWith } from 'rxjs/operators';

import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../../../@core/dialogs/confirmation-dialog/confirmation-dialog.component';
import { QuotesActions } from '../../../@store/quotes/quotes.actions';
import {
  selectAssignees,
  selectCANumbers,
  selectCreatedQuoteLoading,
  selectCustomers,
  selectItemProcessesCategories,
  selectQuotesShippingMethod,
  selectSavedNote,
  selectSelectedCANumber,
  selectSelectedQuote,
  selectSelectedQuoteError,
  selectSelectedQuoteLoading,
  selectStatuses,
  selectUpdatedQuoteError,
  selectUpdatedQuoteLoading,
} from '../../../@store/quotes/quotes.selectors';
import {
  Customer,
  ICableAssemblyOrder,
  IItemProcesses,
  IQuantityBreak,
} from '../../../@store/quotes/quotes.state.interface';
import { selectLoggedInUser } from '../../../@store/user/user.selectors';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';
import { UploadMediaComponent } from '../../@shared/upload-media/upload-media.component';
import { ApproveQuoteForProductionComponent } from '../approve-quote-for-production/approve-quote-for-production.component';
import { CableDiameterCalculatorComponent } from '../cable-diameter-calculator/cable-diameter-calculator.component';
import { CompositeCableCalculatorComponent } from '../composite-cable-calculator/composite-cable-calculator.component';
import { ConstructionTextComponent } from '../construction-text/construction-text.component';
import { QuoteDetailAccordionComponent } from '../quote-detail-accordion/quote-detail-accordion.component';
import { QuoteTotalTabsComponent } from '../quote-total-tabs/quote-total-tabs.component';

@Component({
  selector: 'ciwc-manage-quote',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatExpansionModule,
    MatTableModule,
    MatSortModule,
    MatDialogModule,
    UploadMediaComponent,
    ConstructionTextComponent,
    QuoteTotalTabsComponent,
    QuoteDetailAccordionComponent,
  ],
  providers: [],
  templateUrl: './manage-quote.component.html',
  styleUrl: './manage-quote.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'handleKeyboardShortcut($event)',
  },
})
export class ManageQuoteComponent implements OnInit, OnDestroy {
  @ViewChildren(QuoteDetailAccordionComponent)
  accordions!: QueryList<QuoteDetailAccordionComponent>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly wasQuoteInitialized = signal(false);

  // Selectors would be implemented here
  readonly assignees = this.store.selectSignal(selectAssignees);
  readonly clients = this.store.selectSignal(selectCustomers);
  readonly statuses = this.store.selectSignal(selectStatuses);
  readonly quotesShippingMethod = this.store.selectSignal(selectQuotesShippingMethod);
  readonly caNumbers = this.store.selectSignal(selectCANumbers);
  readonly selectedCANumber = this.store.selectSignal(selectSelectedCANumber);
  readonly itemProcessesCategories = this.store.selectSignal(selectItemProcessesCategories);
  readonly createdQuoteLoading = this.store.selectSignal(selectCreatedQuoteLoading);
  readonly selectedQuote = this.store.selectSignal(selectSelectedQuote);
  readonly selectedQuoteLoading = this.store.selectSignal(selectSelectedQuoteLoading);
  readonly selectedQuoteError = this.store.selectSignal(selectSelectedQuoteError);
  readonly updatedQuoteLoading = this.store.selectSignal(selectUpdatedQuoteLoading);
  readonly updatedQuoteError = this.store.selectSignal(selectUpdatedQuoteError);
  readonly savedNote = this.store.selectSignal(selectSavedNote);
  readonly loggedInUser = this.store.selectSignal(selectLoggedInUser);

  readonly CONFIG = CONFIG;
  readonly allTabs: SafeAny = [];
  readonly isEditMode = signal(false);

  totalPrices = signal([0]);
  isAddQuantityBreakDisabled = signal(true);
  displayNotes = signal<SafeAny[]>([]);
  constructionSheet = signal<SafeAny>([]);
  constructionSteps = signal<SafeAny>([]);
  processedConstructionSteps = signal<SafeAny>([]);
  showConfirmModal = true;
  initiateRedirect = true;
  enableProcessSelectedCANumber = signal(true);
  constructionText = signal('');
  remainingDescriptions = signal<string[]>([]);
  tempStatusValue = signal<string | null>(null);

  quoteForm = this.fb.group({
    // 'caNumber' or 'customQuote'
    quoteType: ['customQuote', Validators.required],
    quantity: [''],
    breakQuoteIntoEach: [false],
    quantityBreaks: this.fb.array([]),
    caNumber: [''],
    caCustomDescription: [''],
    customQuote: [false],

    // Original form controls
    assignee: [{}, Validators.required],
    attachments: this.fb.group({
      attachmentDocument: [''],
      attachmentName: [''],
      id: [''],
      isUploading: [false],
      uploadError: [''],
      url: [''],
    }),
    certsRequired: [false],
    cfm: [false],
    ciwToSupplyWire: [false],
    client: [''],
    description: [''],
    estShipDate: [''],
    feetPerReel: ['', Validators.required],
    shippingMethod: ['Notify'],
    status: ['', Validators.required],
    testReports: [false],
    notes: [''],
  });

  get attachments() {
    return this.quoteForm.get('attachments') as FormGroup;
  }

  get quoteId() {
    return this.route.snapshot.paramMap.get('id');
  }

  private readonly categoryColors: Record<string, string> = {
    OTHER: '#8a8a8a',
    COPPER: '#b73a3a',
    SINGLE_END: '#2e9d50',
    COMPOUNDS: '#a89334',
    TEFLON_TAPES: '#b05a33',
    ALL_TAPES_AND_FILLERS: '#9c4b9c',
  };

  constructor() {
    effect(() => {
      // Update notes based on edit mode and saved note state
      if (this.isEditMode()) {
        const selectedQuote = this.selectedQuote();
        const savedNote = this.savedNote();

        if (selectedQuote && this.itemProcessesCategories()?.length) {
          this.initializeQuoteData();
        }

        if (savedNote) {
          this.displayNotes.set(savedNote || []);
        } else if (selectedQuote?.notes) {
          this.displayNotes.set(selectedQuote.notes);
        }
      }
    });

    effect(() => {
      this.processSelectedCANumber();
    });

    effect(() => {
      if (!this.isEditMode()) {
        const matchingAssignee: SafeAny = this.assignees()?.find(
          (assignee) => assignee.id === +this.loggedInUser()?.id,
        );

        // Patch the assignee to the current user when adding a new quote (not edit mode)
        if (matchingAssignee && this.loggedInUser()) {
          this.quoteForm.patchValue({
            assignee: matchingAssignee,
          });
        }

        return;
      }

      const matchingCANumber: SafeAny = this.caNumbers()?.find(
        (ca) => ca.id === this.selectedQuote()?.caNumber?.id,
      );

      this.quoteForm.patchValue({
        caNumber: matchingCANumber || '',
      });

      if (matchingCANumber) {
        this.store.dispatch(
          QuotesActions.loadCANumberById({
            id: matchingCANumber.id,
            includeRelations: true,
          }),
        );
      }
    });

    effect(() => {
      const selectedQuote = this.selectedQuote();
      const matchingAssignee = this.assignees()?.find(
        (assignee) => assignee.id === selectedQuote?.assignee?.id,
      );

      if (selectedQuote && this.isEditMode()) {
        this.quoteForm.patchValue({
          assignee: matchingAssignee || selectedQuote.assignee,
        });
      }
    });

    effect(() => {
      if (this.quotesShippingMethod()?.length && !this.isEditMode()) {
        this.quoteForm.patchValue({
          shippingMethod:
            this.quotesShippingMethod().find((method: SafeAny) => method.key === 'NOTIFY')?.key ||
            '',
        });
      }
    });
  }

  ngOnInit(): void {
    this.isEditMode.set(!!this.quoteId);

    this.addQuantityBreak();
    this.fetchData();
    this.setupClientFilter();
    this.setupCANumberFilter();
    this.setupCANumberValueChanges();
    this.setupQuantityBreakListener();
    this.setupQuoteTypeListener();
    this.setupStatusListener();
  }

  ngOnDestroy(): void {
    this.store.dispatch(QuotesActions.clearSelectedCANumber());
    this.store.dispatch(QuotesActions.clearSelectedQuote());
    this.store.dispatch(QuotesActions.clearNote());

    this.constructionSheet.set([]);
    this.constructionSteps.set([]);
    this.processedConstructionSteps.set([]);
    this.constructionText.set('');
    this.displayNotes.set([]);
  }

  addQuantityBreak(): void {
    this.quantityBreaksArray.push(
      this.fb.group({
        quantity: [0],
        pieces: [0],
      }),
    );
  }

  fetchData(): void {
    if (this.quoteId) {
      this.store.dispatch(QuotesActions.loadSpecificQuote({ quoteId: +this.quoteId }));
    }

    this.store.dispatch(QuotesActions.loadAssignees({ payload: { category: 'QUOTES' } }));
    this.store.dispatch(QuotesActions.loadStatuses({ payload: { identifier: 'QUOTES_STATUS' } }));
    this.store.dispatch(QuotesActions.loadCustomers());
    this.store.dispatch(
      QuotesActions.loadCANumbers({
        payload: {
          limit: -1,
        },
      }),
    );
    this.store.dispatch(QuotesActions.loadItemProcessesCategories());
    this.store.dispatch(QuotesActions.loadQuoteShippingMethod());
  }

  private setupClientFilter(): void {
    this.filteredOptions$ = this.quoteForm.get('client')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(''),
      map((value) => {
        if (!value || typeof value !== 'string') {
          return this.clients();
        }

        return this.clients()?.filter((customer: Customer) => {
          const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();

          return fullName.includes(value.toLowerCase());
        });
      }),
    );
  }

  private setupCANumberFilter(): void {
    this.filteredCANumbers$ = this.quoteForm.get('caNumber')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((value) => {
        if (!value || typeof value !== 'string') {
          return this.caNumbers();
        }

        return this.caNumbers()?.filter((caNumberObj: SafeAny) =>
          caNumberObj.caNumber.toLowerCase().includes(value.toLowerCase()),
        );
      }),
    );
  }

  private setupCANumberValueChanges(): void {
    this.quoteForm
      .get('caNumber')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((value: SafeAny) => {
        // If the CA Number entered/selected is not an object, do nothing.
        // We need the CA Number id to GET data from the API, otherwise, no API call is being made.
        if (!value?.id) {
          return;
        }

        // Clear all quantity breaks except the first one
        if (this.enableProcessSelectedCANumber()) {
          this.quantityBreaksArray.clear();
          this.addQuantityBreak();
        }

        this.enableProcessSelectedCANumber.set(true);

        if (this.quoteForm.get('caNumber')?.pristine) {
          return;
        }

        this.store.dispatch(
          QuotesActions.loadCANumberById({
            id: value.id,
            includeRelations: true,
          }),
        );
      });
  }

  private setupQuantityBreakListener(): void {
    this.quoteForm
      .get('quantityBreaks')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.refreshAccordions(value));
  }

  private setupQuoteTypeListener(): void {
    // Listen to quoteType form control changes
    this.quoteForm
      .get('quoteType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value === 'customQuote') {
          this.quoteForm.patchValue({
            caNumber: '',
          });
          this.quantityBreaksArray.clear();
          this.addQuantityBreak();
        }
      });

    // Listen to caNumber form control changes
    this.quoteForm
      .get('caNumber')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.isAddQuantityBreakDisabled.set(!value);
      });
  }

  private setupStatusListener(): void {
    this.quoteForm
      .get('status')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), skip(this.isEditMode() ? 1 : 0))
      .subscribe((value) => {
        if (value === 'IN_PRODUCTION') {
          const quantityBreaks = this.quoteForm.get('quantityBreaks')?.value;
          const hasPieces = this.quoteForm.get('breakQuoteIntoEach')?.value;

          // Add index to each quantity break
          const quantityBreaksWithIndex = quantityBreaks?.map((qb: SafeAny, index: number) => ({
            ...qb,
            index,
          }));

          const tabsValues = this.allTabs.map((tab: SafeAny) => ({
            ...tab,
            quantityBreak: quantityBreaksWithIndex?.[tab.index]?.quantity,
            pieces: quantityBreaksWithIndex?.[tab.index]?.pieces,
          }));

          const dialogRef = this.dialog.open(ApproveQuoteForProductionComponent, {
            width: '800px',
            disableClose: true,
            data: {
              hasPieces,
              quoteId: this.quoteId,
              tabsValues,
            },
          });

          dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
              if (result) {
                this.showConfirmModal = false;

                this.router.navigate([`/orders/${result}/view`]);
              } else {
                this.quoteForm.get('status')?.setValue(this.tempStatusValue());
              }
            });
        } else {
          this.tempStatusValue.set(value);
        }
      });
  }

  private calculateTotalPriceForBreak(index: number): number {
    let totalPrice = 0;

    // Process each category's data
    [
      { data: this.copperData(), type: 'COPPER' },
      { data: this.singleEndData(), type: 'SINGLE_END' },
      { data: this.fillersAndMiscLaborData(), type: 'OTHER' },
      { data: this.compoundsData(), type: 'COMPOUNDS' },
      { data: this.teflonTapesData(), type: 'TEFLON_TAPES' },
      { data: this.allTapesAndFillersData(), type: 'ALL_TAPES_AND_FILLERS' },
    ].forEach((category) => {
      category.data.forEach((item) => {
        // For index 0, use parent items
        // For other indices, use child items with matching index
        if (
          (index === 0 && item.isParent) ||
          (index > 0 && !item.isParent && String(item.uniqueId).endsWith(`_${index}`))
        ) {
          totalPrice += Number(item.sellPrice) || 0;
        }
      });
    });

    return totalPrice;
  }

  getQuoteTotalTabsForBreak(index: number): SafeAny[] {
    const breaks = this.quantityBreaksArray.value;
    const currentBreak = breaks[index];
    const quantity = currentBreak?.quantity ?? 0;
    const pieces = currentBreak?.pieces ?? 0;
    const breakQuoteIntoEach = this.quoteForm.get('breakQuoteIntoEach')?.value;

    // Get the total price for this break
    const totalPrice = this.calculateTotalPriceForBreak(index);

    const tabs = [
      { quantity: quantity, label: 'Qty', suffix: 'ft', index: 0 },
      {
        firstValue: +totalPrice.toFixed(2) || 0,
        secondValue: quantity || 0,
        suffix: 'ft',
        index: 1,
      },
      {
        firstValue: quantity ? +(totalPrice / quantity)?.toFixed(3) || 0 : 0,
        secondValue: 1,
        suffix: 'ft',
        index: 2,
      },
    ];

    if (breakQuoteIntoEach) {
      tabs.push({
        firstValue: pieces ? +(totalPrice / pieces)?.toFixed(3) || 0 : 0,
        secondValue: null,
        suffix: 'EA',
        index: 3,
      });
    }

    const newTab = {
      index,
      pricePerFeet: tabs[2].firstValue,
      pricePerEA: breakQuoteIntoEach
        ? pieces
          ? +(totalPrice / pieces)?.toFixed(3) || 0
          : 0
        : null,
      quantityBreak: quantity,
    };

    // Remove any existing tabs with the same index
    const filteredTabs = this.allTabs.filter((tab: SafeAny) => tab.index !== index);

    // Add the new tab
    filteredTabs.push(newTab);

    // Update allTabs with the filtered array
    this.allTabs.length = 0;
    this.allTabs.push(...filteredTabs);

    return tabs;
  }

  // Add a method to update total prices that can be called from event handlers
  updateTotalPrices(): void {
    const breaks = this.quantityBreaksArray.value;
    const newPrices = breaks.map((_: SafeAny, index: number) =>
      this.calculateTotalPriceForBreak(index),
    );
    this.totalPrices.set(newPrices);
  }

  private initializeQuoteData(): void {
    const selectedQuote = this.selectedQuote();

    if (!selectedQuote || this.wasQuoteInitialized() || !this.isEditMode()) {
      return;
    }

    this.quoteForm.patchValue({
      attachments: {
        attachmentDocument: selectedQuote.attachments[0]?.document,
        attachmentName: selectedQuote.attachments[0]?.name,
        url: selectedQuote.attachments[0]?.url,
        id: null,
        isUploading: false,
        uploadError: null,
      },
      breakQuoteIntoEach: selectedQuote.hasPieces,
      caCustomDescription: selectedQuote.caCustomDescription,
      certsRequired: selectedQuote.certsRequired,
      cfm: selectedQuote.cfm,
      ciwToSupplyWire: selectedQuote.ciwToSupplyWire,
      client: selectedQuote.client,
      description: selectedQuote.description,
      estShipDate: selectedQuote.estShipDate,
      feetPerReel: selectedQuote.feetPerReel,
      quantity: selectedQuote.quantity,
      quoteType:
        selectedQuote.caCustomDescription && !selectedQuote.caNumber ? 'customQuote' : 'caNumber',
      shippingMethod: selectedQuote.shippingMethod.key,
      status: selectedQuote.status.key,
      testReports: selectedQuote.testReports,
      notes: '',
    });

    this.quantityBreaksArray.clear();

    // Add quantity breaks to the form array
    if (selectedQuote.quantityBreaks?.length) {
      selectedQuote.quantityBreaks.forEach((breakItem: IQuantityBreak) => {
        this.quantityBreaksArray.push(
          this.fb.group({
            quantity: [breakItem.quantity || 0],
            pieces: [breakItem.pieces || 0],
          }),
        );
      });
    } else {
      // Add default quantity break if none exist
      this.quantityBreaksArray.push(
        this.fb.group({
          quantity: [selectedQuote.quantity || 0],
          pieces: [0],
        }),
      );
    }

    this.createTabsTotalPrices(this.quantityBreaksArray.value.length);
    this.setDetailsAccordionData(selectedQuote.itemProcesses);
    this.refreshAccordions(this.quoteForm.get('quantityBreaks')?.value, true);

    this.wasQuoteInitialized.set(true);
    this.tempStatusValue.set(selectedQuote.status.key);
  }

  private createTabsTotalPrices(quantityBreaksLength: number): void {
    if (!quantityBreaksLength) return;

    const totalPrices = [];

    for (let i = 0; i < quantityBreaksLength; i++) {
      totalPrices.push(0);
    }

    this.totalPrices.set(totalPrices as never[]); // Type assertion to fix type error
  }

  private setDetailsAccordionData(itemProcesses: SafeAny): void {
    if (!itemProcesses) return;

    // Set all data at once to prevent multiple signal updates
    this.copperData.set(itemProcesses['COPPER'] || []);
    this.singleEndData.set(itemProcesses['SINGLE_END'] || []);
    this.fillersAndMiscLaborData.set(itemProcesses['OTHER'] || []);
    this.compoundsData.set(itemProcesses['COMPOUNDS'] || []);
    this.teflonTapesData.set(itemProcesses['TEFLON_TAPES'] || []);
    this.allTapesAndFillersData.set(itemProcesses['ALL_TAPES_AND_FILLERS'] || []);
  }

  private updateAccordions(shouldUpdateAccordions = true): void {
    const categories = this.itemProcessesCategories() || [];
    const accordions = categories.map(
      (category: { value: string; color?: string; key: string }, index: number) => {
        const categoryData = this.getDataForCategory(category.key);
        // Add isParent property to objects that don't have it
        const processedData = categoryData.map((item) => ({
          ...item,
          isParent: Object.prototype.hasOwnProperty.call(item, 'isParent') ? item.isParent : true,
        }));

        return {
          title: category.value,
          color: this.categoryColors?.[category.key] || category.color || '#8a8a8a',
          key: category.key,
          data: processedData,
          index,
        };
      },
    );

    if (!accordions.length) return;

    if (shouldUpdateAccordions) {
      this.quoteDetailAccordions.set(accordions);
    }

    // Update total prices after updating accordions
    this.updateTotalPrices();
  }

  private calculateTotalPricesAndSetAccordions(
    accordions: {
      data: { isParent?: boolean; sellPrice?: number; uniqueId?: string }[];
      title: string;
      color: string;
      key: string;
      index: number;
    }[],
  ): void {
    // Get current prices to preserve child values
    const currentPrices = this.totalPrices();
    const newPrices = [...currentPrices];

    // Only calculate for parent items
    let parentSum = 0;
    accordions.forEach((accordion) => {
      accordion.data.forEach((item) => {
        if (item.sellPrice && item.isParent) {
          parentSum += Number(item.sellPrice);
        }
      });
    });

    // Only update the parent total (index 0)
    newPrices[0] = parentSum;

    // Update totalPrices, preserving all child values
    this.totalPrices.update(() => newPrices);
  }

  private getDataForCategory(key: string): SafeAny[] {
    switch (key) {
      case 'OTHER':
        return this.fillersAndMiscLaborData();
      case 'COPPER':
        return this.copperData();
      case 'SINGLE_END':
        return this.singleEndData();
      case 'COMPOUNDS':
        return this.compoundsData();
      case 'TEFLON_TAPES':
        return this.teflonTapesData();
      case 'ALL_TAPES_AND_FILLERS':
        return this.allTapesAndFillersData();
      default:
        return [];
    }
  }

  displayFn = (customer: Customer | null): string => {
    if (!customer) return '';

    return `${customer.firstName} ${customer.lastName}`;
  };

  displayCANumber = (caNumber: SafeAny | null): string => {
    if (!caNumber) return '';

    return caNumber.caNumber;
  };

  filteredOptions$: Observable<SafeAny> | undefined;
  filteredCANumbers$: Observable<SafeAny> | undefined;
  displayedColumns = ['item', 'description', 'quantity', 'unitPrice', 'total'];

  fillersAndMiscLaborData = signal<SafeAny[]>([]);
  copperData = signal<SafeAny[]>([]);
  singleEndData = signal<SafeAny[]>([]);
  compoundsData = signal<SafeAny[]>([]);
  teflonTapesData = signal<SafeAny[]>([]);
  allTapesAndFillersData = signal<SafeAny[]>([]);

  quoteDetailAccordions = signal(
    this.itemProcessesCategories()?.map(
      (category: { name: string; color?: string; key: string }, index: number) => ({
        title: category.name,
        color: this.categoryColors[category.key] || category.color || '#8a8a8a',
        key: category.key,
        data: this.getDataForCategory(category.key),
        index,
      }),
    ),
  );

  // Add signal to track field updates
  fieldUpdates = signal<
    Record<
      string,
      Record<
        string,
        {
          units?: number;
          quantityBreakMargin?: number;
          setup?: number;
          innerDiameter?: number;
          outerDiameter?: number;
          sellPrice?: number;
          quantityBreak?: number;
          ftNeeded?: number;
          pricePerUnit?: number;
        }
      >
    >
  >({});

  get quantityBreaksArray() {
    return this.quoteForm.get('quantityBreaks') as FormArray;
  }

  removeQuantityBreak(index: number): void {
    this.quantityBreaksArray.removeAt(index);
  }

  onAddItemProcessToQuoteClick(): void {
    const dialogRef = this.dialog.open(AddItemProcessToQuoteDialogComponent, {
      width: '600px',
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.onItemProcessAddedCallback(result);
        }
      });
  }

  private onItemProcessAddedCallback(result: SafeAny): void {
    const categoryKey = result.itemType;
    const dataSignal = this.getDataSignalForCategory(categoryKey);

    if (dataSignal) {
      dataSignal.update((data: SafeAny[]) => {
        // Find the highest existing uniqueId number
        const highestId = data.reduce((max, item) => {
          const baseId = String(item.uniqueId || '').split('_')[0];
          const numId = Number(baseId) || 0;
          return Math.max(max, numId);
        }, 0);

        // Create new uniqueId by incrementing the highest
        const newUniqueId = highestId + 1;

        return [
          ...data,
          {
            ...result,
            basePrice: result.basePrice || 0,
            lbPer1000ft: result.lbPer1000ft || 0,
            surcharge: result.surcharge || 0,
            specificGravity: result.specificGravity || 0,
            constant1: result.constant1 || 0,
            basePricePerUnit: result.basePricePerUnit || 0,
            pricePerUnit: result.pricePerUnit || 0,
            pricePerLb: result.pricePerLb || 0,
            uniqueIdentifier: result.itemId || '',
            uniqueId: newUniqueId,
            quantityBreakMargin: result.quantityBreakMargin || null, // Leave empty initially
            setup: result.setup || null, // Leave empty initially
          },
        ];
      });
    }

    this.refreshAccordions(this.quoteForm.get('quantityBreaks')?.value);
  }

  private processDataWithItemType(data: SafeAny[], itemType: string): SafeAny[] {
    const result: SafeAny[] = [];
    let parentIndex = -1;

    for (let i = 0; i < data.length; i++) {
      const item = { ...data[i] };

      if (item.isParent === true) {
        parentIndex++;
        item.uniqueId = parentIndex.toString();
        result.push(item);

        // Now, look ahead for children of this parent
        let childIndex = 1;
        for (let j = i + 1; j < data.length; j++) {
          const child = data[j];
          if (child.isParent === false) {
            const childCopy = { ...child, uniqueId: `${parentIndex}_${childIndex}` };
            result.push(childCopy);
            childIndex++;
          } else {
            // Next parent found, break to outer loop
            break;
          }
        }
      }
    }

    // Optionally, add itemType and ftNeeded as before
    return result.map((item) => ({
      ...item,
      ftNeeded: item.quantityBreak,
      itemType,
    }));
  }

  private refreshAccordions(value: SafeAny, fromInitData = false): void {
    const breaks = value as { quantity: number; pieces: number }[];

    if (!breaks?.length) {
      return;
    }

    const firstQuantity = breaks[0].quantity ?? 0;

    // If fromInitData is true, just update the data arrays without processing
    if (fromInitData) {
      this.copperData.update((data) => this.processDataWithItemType(data, 'COPPER'));
      this.singleEndData.update((data) => this.processDataWithItemType(data, 'SINGLE_END'));
      this.fillersAndMiscLaborData.update((data) => this.processDataWithItemType(data, 'OTHER'));
      this.compoundsData.update((data) => this.processDataWithItemType(data, 'COMPOUNDS'));
      this.teflonTapesData.update((data) => this.processDataWithItemType(data, 'TEFLON_TAPES'));
      this.allTapesAndFillersData.update((data) =>
        this.processDataWithItemType(data, 'ALL_TAPES_AND_FILLERS'),
      );
      this.updateAccordions();
      this.updateTotalPrices();

      return;
    }

    // Update accordion data based on quantity breaks length
    const updateAccordionData = (data: SafeAny[]) => {
      if (!data || data.length === 0) return data;

      const result: SafeAny[] = [];

      // Process each parent item and its children
      data.forEach((item, index) => {
        const uniqueId = String(item.uniqueId || '');
        const isExistingParent = !uniqueId.includes('_');

        if (isExistingParent) {
          // Add parent
          const parentItem = {
            ...item,
            ftNeeded: firstQuantity,
            isParent: true,
            uniqueId: item.uniqueId || item.id || index.toString(),
          };
          result.push(parentItem);

          // Find existing children for this parent
          const existingChildren = data.filter((child) => {
            const childId = String(child.uniqueId || '');
            return childId.startsWith(parentItem.uniqueId + '_') && !child.isParent;
          });

          // Handle children for both new and existing parents
          if (breaks.length > 1) {
            // Process each quantity break after the first one
            for (let i = 1; i < breaks.length; i++) {
              // Try to find existing child for this break
              const existingChild = existingChildren.find(
                (child) => child.uniqueId === `${parentItem.uniqueId}_${i}`,
              );

              if (existingChild) {
                // Update existing child's ftNeeded while preserving other values
                const updatedChild = {
                  ...existingChild,
                  ftNeeded: Number(breaks[i].quantity) || 0,
                };
                result.push(updatedChild);
              } else {
                // Create new child item
                const childItem = {
                  ...item,
                  isParent: false,
                  uniqueId: `${parentItem.uniqueId}_${i}`,
                  ftNeeded: Number(breaks[i].quantity) || 0,
                  quantityBreakMargin: item.quantityBreakMargin,
                  setup: item.setup,
                  totalLbs: item.totalLbs,
                  sellPrice: item.sellPrice,
                };
                result.push(childItem);
              }
            }
          }
        }
      });

      // Sort the result array to group parents with their children
      return result.sort((a, b) => {
        const aId = String(a.uniqueId || '');
        const bId = String(b.uniqueId || '');

        // Get base IDs (before underscore) and whether they're children
        const aBaseId = aId.split('_')[0];
        const bBaseId = bId.split('_')[0];
        const aIsChild = aId.includes('_');
        const bIsChild = bId.includes('_');

        // If they have different base IDs, sort by base ID
        if (aBaseId !== bBaseId) {
          return Number(aBaseId) - Number(bBaseId);
        }

        // If they have the same base ID, parents come before children
        if (aIsChild !== bIsChild) {
          return aIsChild ? 1 : -1;
        }

        // If both are children, sort by their suffix number
        if (aIsChild && bIsChild) {
          const aSuffix = Number(aId.split('_')[1]);
          const bSuffix = Number(bId.split('_')[1]);
          return aSuffix - bSuffix;
        }

        return 0;
      });
    };

    // Update each data array
    this.copperData.update((data) => {
      const currentData = Array.isArray(data)
        ? data.map((item) => ({ ...item, itemType: 'COPPER' }))
        : [];
      return updateAccordionData(currentData);
    });
    this.singleEndData.update((data) => {
      const currentData = Array.isArray(data)
        ? data.map((item) => ({ ...item, itemType: 'SINGLE_END' }))
        : [];
      return updateAccordionData(currentData);
    });
    this.fillersAndMiscLaborData.update((data) => {
      const currentData = Array.isArray(data)
        ? data.map((item) => ({ ...item, itemType: 'OTHER' }))
        : [];
      return updateAccordionData(currentData);
    });
    this.compoundsData.update((data) => {
      const currentData = Array.isArray(data)
        ? data.map((item) => ({ ...item, itemType: 'COMPOUNDS' }))
        : [];
      return updateAccordionData(currentData);
    });
    this.teflonTapesData.update((data) => {
      const currentData = Array.isArray(data)
        ? data.map((item) => ({ ...item, itemType: 'TEFLON_TAPES' }))
        : [];
      return updateAccordionData(currentData);
    });
    this.allTapesAndFillersData.update((data) => {
      const currentData = Array.isArray(data)
        ? data.map((item) => ({ ...item, itemType: 'ALL_TAPES_AND_FILLERS' }))
        : [];
      return updateAccordionData(currentData);
    });

    // Update the accordions to reflect changes
    this.updateAccordions();
  }

  private getDataSignalForCategory(key: string): SafeAny {
    const categories = this.itemProcessesCategories();

    if (!categories) return null;

    const categoryMap: SafeAny = {
      OTHER: this.fillersAndMiscLaborData,
      COPPER: this.copperData,
      SINGLE_END: this.singleEndData,
      COMPOUNDS: this.compoundsData,
      TEFLON_TAPES: this.teflonTapesData,
      ALL_TAPES_AND_FILLERS: this.allTapesAndFillersData,
    };

    // Verify the key exists in our categories
    const categoryExists = categories.some((category: { key: string }) => category.key === key);

    if (!categoryExists) {
      console.warn('Unknown category:', key);

      return null;
    }

    return categoryMap[key] || null;
  }

  onSubmit(): void {
    this.showConfirmModal = false;

    const payload = this.buildPayload(this.quoteForm.getRawValue());

    if (this.quoteId) {
      this.store.dispatch(
        QuotesActions.updateQuote({
          quoteId: this.quoteId,
          payload,
          initiateRedirect: this.initiateRedirect,
        }),
      );
    } else {
      this.store.dispatch(QuotesActions.createQuote({ payload }));
    }
  }

  private buildPayload(formValues: SafeAny): ICableAssemblyOrder {
    const { assignee, client, caNumber: caNumberValue } = formValues;

    return {
      ...formValues,
      assignee: assignee.id,
      dueDate: this.calculateDueDate(),
      client: this.buildClientData(client),
      status: formValues.status || '',
      estShipDate: formValues.estShipDate,
      quantity: formValues.quantity?.toString() || '100',
      hasPieces: formValues.breakQuoteIntoEach ?? true,
      caNumber: formValues.quoteType === 'caNumber' ? caNumberValue.id : null,
      caCustomDescription:
        formValues.quoteType === 'customQuote' ? formValues.caCustomDescription || '' : '',
      customQuote: formValues.quoteType === 'customQuote',
      itemProcesses: this.buildItemProcesses(),
      quantityBreaks: formValues.quantityBreaks as IQuantityBreak[],
      attachments: this.buildAttachments(formValues.attachments),
      notes: formValues.notes || '',
      certsRequired: formValues.certsRequired ?? false,
      testReports: formValues.testReports ?? false,
      cfm: formValues.cfm ?? false,
      ciwToSupplyWire: formValues.ciwToSupplyWire ?? false,
      description: formValues.description || '',
      shippingMethod: formValues.shippingMethod || 'Notify',
      feetPerReel: formValues.feetPerReel || '',
    };
  }

  private buildItemProcesses(): IItemProcesses {
    const fieldUpdates = this.fieldUpdates();

    return {
      OTHER: this.transformItems(this.fillersAndMiscLaborData(), fieldUpdates['OTHER']),
      COPPER: this.transformItems(this.copperData(), fieldUpdates['COPPER'], ['outerDiameter']),
      SINGLE_END: this.transformItems(this.singleEndData(), fieldUpdates['SINGLE_END'], [
        'outerDiameter',
      ]),
      COMPOUNDS: this.transformItems(this.compoundsData(), fieldUpdates['COMPOUNDS'], [
        'outerDiameter',
      ]),
      TEFLON_TAPES: this.transformItems(this.teflonTapesData(), fieldUpdates['TEFLON_TAPES'], [
        'outerDiameter',
      ]),
      ALL_TAPES_AND_FILLERS: this.transformItems(
        this.allTapesAndFillersData(),
        fieldUpdates['ALL_TAPES_AND_FILLERS'],
        ['outerDiameter'],
      ),
    };
  }

  private transformItems(
    items: SafeAny[],
    updates: SafeAny = {},
    extraFields: string[] = [],
  ): SafeAny[] {
    return items.map((item) => {
      const itemUpdates = updates[item.uniqueId ?? ''] || {};

      const baseTransform = {
        ...item,
        units: itemUpdates.units ?? item.units ?? 0,
        quantityBreakMargin: itemUpdates.quantityBreakMargin ?? item.quantityBreakMargin ?? 0,
        setup: itemUpdates.setup ?? item.setup ?? 0,
        isParent: itemUpdates.isParent ?? item.isParent ?? true,
        innerDiameter: this.resolveDiameter(itemUpdates.innerDiameter, item.innerDiameter),
        outerDiameter: this.resolveDiameter(itemUpdates.outerDiameter, item.outerDiameter),
        sellPrice: itemUpdates.sellPrice ?? item.sellPrice ?? 0,
        quantityBreak: itemUpdates.quantityBreak ?? item.ftNeeded ?? 0,
      };

      // Add extra fields if specified
      extraFields.forEach((field) => {
        if (field === 'outerDiameter') {
          baseTransform[field] = this.resolveDiameter(
            itemUpdates.outerDiameter,
            item.outerDiameter,
          );
        }

        if (field === 'pricePerUnit') {
          baseTransform[field] = itemUpdates[field] ?? item[field] ?? 0;
        }
      });

      // Handle OTHER type specific fields
      if (!extraFields.includes('outerDiameter')) {
        baseTransform.pricePerUnit = itemUpdates.pricePerUnit ?? item.pricePerUnit ?? 0;
      }

      return baseTransform;
    });
  }

  private resolveDiameter(updateValue: SafeAny, originalValue: SafeAny) {
    if (updateValue !== undefined && updateValue !== null) {
      return updateValue;
    }

    if (
      originalValue !== null &&
      originalValue !== undefined &&
      !(Array.isArray(originalValue) && originalValue[0] === null)
    ) {
      return originalValue;
    }

    return null;
  }

  private calculateDueDate(): string {
    return new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];
  }

  private buildClientData(client: SafeAny) {
    return {
      id: client.id,
      code: client.customerCode || client.code,
      address: client.customerAddress || client.address,
    };
  }

  private formatEstimatedShipDate(estShipDate: SafeAny): string {
    return estShipDate
      ? new DatePipe('en-US').transform(estShipDate, CONFIG.date.formatISO) || ''
      : '';
  }

  private buildAttachments(attachments: SafeAny) {
    return attachments.attachmentName || attachments.attachmentDocument
      ? [
          {
            name: attachments.attachmentName || '',
            document: attachments.attachmentDocument || '',
          },
        ]
      : [];
  }

  goBack(): void {
    const dialogData: ConfirmationDialogData = {
      message: 'Are you sure you want to cancel these changes?',
      confirmText: 'YES',
      cancelText: 'NO',
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
      disableClose: true,
      width: '500px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.showConfirmModal = false;
          this.router.navigate(['/quotes']);
        }
      });
  }

  openCableDiameterCalculator(): void {
    this.dialog.open(CableDiameterCalculatorComponent, {
      width: '500px',
      disableClose: true,
    });
  }

  openCompositeCableCalculator(): void {
    this.dialog.open(CompositeCableCalculatorComponent, {
      width: '800px',
      disableClose: true,
    });
  }

  private processSelectedCANumber(): void {
    const selectedCANumber = this.selectedCANumber();

    if (!selectedCANumber || !selectedCANumber?.constructionSteps?.[0]) {
      return;
    }

    const firstConstructionStep = selectedCANumber.constructionSteps[0];
    this.constructionText.set(firstConstructionStep.description);

    // Define properties that should be excluded from array processing
    const excludedKeys = ['constructionSteps', 'attachments', 'itemProcesses'];

    // Extract all array properties from the selected CA number, excluding specified keys
    const arrayProperties = Object.entries(selectedCANumber)
      .filter(([key, value]) => Array.isArray(value) && !excludedKeys.includes(key))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // Extract array properties from the first construction step
    const firstConstructionStepArrayProperties: Record<string, string[]> = Object.entries(
      firstConstructionStep,
    )
      .filter(([key, value]) => Array.isArray(value) && !excludedKeys.includes(key))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // This creates a flat array of options with name, label, checked status, and item value
    const newOptions = Object.entries(arrayProperties).flatMap(([key, value]) => {
      if (!Array.isArray(value)) {
        return [];
      }

      // Create a capitalized label for the option
      const label = key.charAt(0).toUpperCase() + key.slice(1);
      // Get corresponding array from first construction step
      const firstStepArray = firstConstructionStepArrayProperties[key] || [];

      // Create options array - either a single "N/A" option or mapped array of values
      const resultValue: SafeAny =
        value.length === 0
          ? [
              {
                name: key,
                label: `${label}:`,
                checked: false,
                item: null,
              },
            ]
          : value.map((item: string) => ({
              name: key,
              label: `${label}: ${item || ''}`,
              checked: item && firstStepArray.includes(item),
              item,
            }));

      return resultValue;
    });

    // Update construction sheet signal if we have new options
    if (newOptions.length > 0) {
      this.constructionSheet.set(newOptions);
    }

    // Get remaining construction steps (all except the first one)
    const remainingConstructionSteps = selectedCANumber.constructionSteps.slice(1);

    const remainingDescriptions: string[] = remainingConstructionSteps.map(
      (step: SafeAny) => step.description,
    );

    this.remainingDescriptions.set(remainingDescriptions);

    // Process each remaining construction step through the same transformation
    const processedSteps = remainingConstructionSteps.map((step) => {
      // Extract array properties from the current step
      const stepArrayProperties: Record<string, string[]> = Object.entries(selectedCANumber)
        .filter(([key, value]) => Array.isArray(value) && !excludedKeys.includes(key))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Transform each step's properties into options
      return Object.entries(step).flatMap(([key, value]) => {
        if (!Array.isArray(value)) {
          return [];
        }

        const label = key.charAt(0).toUpperCase() + key.slice(1);
        const firstStepArray = stepArrayProperties[key] || [];

        // Create options for each array property in the step
        return value.length === 0
          ? [
              {
                name: key,
                label: `${label}: N/A`,
                checked: false,
                item: null as string | null,
              },
            ]
          : value.map((item: string) => ({
              name: key,
              label: `${label}: ${item || 'N/A'}`,
              checked: firstStepArray.includes(item),
              item: item as string | null,
            }));
      });
    });

    // Update the processed construction steps signal with the transformed data
    this.processedConstructionSteps.set(processedSteps);

    if (!this.quoteForm.get('caNumber')?.pristine && this.enableProcessSelectedCANumber()) {
      this.copperData.set(
        selectedCANumber?.itemProcesses
          ?.filter((item) => item.itemType === 'COPPER')
          .map((item) => {
            const safeItem = item as SafeAny;
            return {
              ...item,
              uniqueId: String(item.id),
              quantityBreakMargin: safeItem.quantityBreakMargin || 0,
              setup: safeItem.setup || 0,
              sellPrice: safeItem.sellPrice || 0,
            };
          }) || [],
      );

      this.singleEndData.set(
        selectedCANumber?.itemProcesses
          ?.filter((item) => item.itemType === 'SINGLE_END')
          .map((item) => {
            const safeItem = item as SafeAny;
            return {
              ...item,
              uniqueId: String(item.id),
              quantityBreakMargin: safeItem.quantityBreakMargin || 0,
              setup: safeItem.setup || 0,
              sellPrice: safeItem.sellPrice || 0,
            };
          }) || [],
      );

      this.fillersAndMiscLaborData.set(
        selectedCANumber?.itemProcesses
          ?.filter((item) => item.itemType === 'OTHER')
          .map((item) => {
            const safeItem = item as SafeAny;
            return {
              ...item,
              uniqueId: String(item.id),
              quantityBreakMargin: safeItem.quantityBreakMargin || 0,
              setup: safeItem.setup || 0,
              sellPrice: safeItem.sellPrice || 0,
            };
          }) || [],
      );

      this.compoundsData.set(
        selectedCANumber?.itemProcesses
          ?.filter((item) => item.itemType === 'COMPOUNDS')
          .map((item) => {
            const safeItem = item as SafeAny;
            return {
              ...item,
              uniqueId: String(item.id),
              quantityBreakMargin: safeItem.quantityBreakMargin || 0,
              setup: safeItem.setup || 0,
              sellPrice: safeItem.sellPrice || 0,
            };
          }) || [],
      );

      this.teflonTapesData.set(
        selectedCANumber?.itemProcesses
          ?.filter((item) => item.itemType === 'TEFLON_TAPES')
          .map((item) => {
            const safeItem = item as SafeAny;
            return {
              ...item,
              uniqueId: String(item.id),
              quantityBreakMargin: safeItem.quantityBreakMargin || 0,
              setup: safeItem.setup || 0,
              sellPrice: safeItem.sellPrice || 0,
            };
          }) || [],
      );

      this.allTapesAndFillersData.set(
        selectedCANumber?.itemProcesses
          ?.filter((item) => item.itemType === 'ALL_TAPES_AND_FILLERS')
          .map((item) => {
            const safeItem = item as SafeAny;
            return {
              ...item,
              uniqueId: String(item.id),
              quantityBreakMargin: safeItem.quantityBreakMargin || 0,
              setup: safeItem.setup || 0,
              sellPrice: safeItem.sellPrice || 0,
            };
          }) || [],
      );

      this.refreshAccordions(this.quoteForm.get('quantityBreaks')?.value);
    }
  }

  onDeleteQuoteDetail(event: { item: SafeAny; key: string }): void {
    const { item, key } = event;

    switch (key) {
      case 'OTHER':
        this.fillersAndMiscLaborData.update((data) =>
          data.filter((i) => i.uniqueId !== item.uniqueId),
        );
        break;
      case 'COPPER':
        this.copperData.update((data) => data.filter((i) => i.uniqueId !== item.uniqueId));
        break;
      case 'SINGLE_END':
        this.singleEndData.update((data) => data.filter((i) => i.uniqueId !== item.uniqueId));
        break;
      case 'COMPOUNDS':
        this.compoundsData.update((data) => data.filter((i) => i.uniqueId !== item.uniqueId));
        break;
      case 'TEFLON_TAPES':
        this.teflonTapesData.update((data) => data.filter((i) => i.uniqueId !== item.uniqueId));
        break;
      case 'ALL_TAPES_AND_FILLERS':
        this.allTapesAndFillersData.update((data) =>
          data.filter((i) => i.uniqueId !== item.uniqueId),
        );
        break;
    }

    // Update the accordions signal to reflect the changes
    this.updateAccordions();
  }

  onAccordionUpdate(event: { item: SafeAny; key: string }): void {
    const { item, key } = event;

    switch (key) {
      case 'OTHER':
        this.fillersAndMiscLaborData.update((data) =>
          data.map((i) => (i.uniqueId === item.uniqueId ? item : i)),
        );
        break;
      case 'COPPER':
        this.copperData.update((data) =>
          data.map((i) => (i.uniqueId === item.uniqueId ? item : i)),
        );
        break;
      case 'SINGLE_END':
        this.singleEndData.update((data) =>
          data.map((i) => (i.uniqueId === item.uniqueId ? item : i)),
        );
        break;
      case 'COMPOUNDS':
        this.compoundsData.update((data) =>
          data.map((i) => (i.uniqueId === item.uniqueId ? item : i)),
        );
        break;
      case 'TEFLON_TAPES':
        this.teflonTapesData.update((data) =>
          data.map((i) => (i.uniqueId === item.uniqueId ? item : i)),
        );
        break;
      case 'ALL_TAPES_AND_FILLERS':
        this.allTapesAndFillersData.update((data) =>
          data.map((i) => (i.uniqueId === item.uniqueId ? item : i)),
        );
        break;
    }

    // Update the accordions signal to reflect the changes
    this.updateAccordions();
  }

  onFieldUpdate(event: {
    item: SafeAny;
    key: string;
    field: string;
    value: number | string;
  }): void {
    const { item, key, field, value } = event;

    // Update the fieldUpdates signal
    this.fieldUpdates.update((updates) => {
      const categoryUpdates = updates[key] || {};
      const itemUpdates = categoryUpdates[item.uniqueId] || {};

      return {
        ...updates,
        [key]: {
          ...categoryUpdates,
          [item.uniqueId]: {
            ...itemUpdates,
            [field]: Number(value) || 0,
            sellPrice: Number(item.sellPrice) || 0,
          },
        },
      };
    });
  }

  onAccordionDataSourceChange(event: { data: SafeAny[]; itemType: string }): void {
    this.enableProcessSelectedCANumber.set(false);

    const { data, itemType } = event;

    switch (itemType) {
      case 'OTHER':
        this.fillersAndMiscLaborData.set(data);
        break;
      case 'COPPER':
        this.copperData.set(data);
        break;
      case 'SINGLE_END':
        this.singleEndData.set(data);
        break;
      case 'COMPOUNDS':
        this.compoundsData.set(data);
        break;
      case 'TEFLON_TAPES':
        this.teflonTapesData.set(data);
        break;
      case 'ALL_TAPES_AND_FILLERS':
        this.allTapesAndFillersData.set(data);
        break;
    }

    // Reset all total prices to 0
    this.totalPrices.update((prices) => {
      return prices.map(() => 0);
    });

    this.updateAccordions(false);
  }

  saveNote(): void {
    const notes = this.quoteForm.get('notes')?.value;

    if (notes && this.quoteId) {
      this.store.dispatch(QuotesActions.saveNote({ quoteId: this.quoteId, notes }));
    }

    this.quoteForm.get('notes')?.reset();
  }

  handleKeyboardShortcut(event: KeyboardEvent): void {
    if (event.key === 'a' || event.key === 'A') {
      // Don't trigger if user is typing in an input field, textarea, or contenteditable element
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement instanceof HTMLSelectElement ||
        activeElement?.hasAttribute('contenteditable') ||
        activeElement?.tagName === 'MAT-INPUT'
      ) {
        return;
      }

      // Only prevent default and trigger shortcut if not in an editable field
      event.preventDefault();
      this.onAddItemProcessToQuoteClick();
    }
  }
}
